import { put, takeEvery, select, call, race, take, all } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { catalogGet, catalogGetFailure, catalogGetSuccess } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { KnmHistoryOperationsEnum } from 'APIPackage/KnmModule/enums/KnmHistoryOperationsEnum';
import { KNM_HISTORY_FIELD_TEXT_PREFIX } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFieldTextPrefix';
import { knmHistoryFieldOperationsMap } from 'KnmPackage/KnmHistoriesModule/constants/knmHistoryFieldOperationsMap';
import { IKnmHistoryItem } from 'APIPackage/KnmModule/interfaces/IKnmHistoryItem';
import { IKnmHistoryItemChanges } from 'KnmPackage/KnmHistoriesModule/interfaces/IKnmHistoryItemChanges';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { uuidRegExp } from 'KnmPackage/MainModule/constants/uuidRegExp';
import {
  createOrUpdatePublishRulesByCode,
  deletePublishRulesByCode,
  fetchPublishRulesByCode,
  fetchPublishRulesHaveSavedSettingsCategories,
  fetchPublishRulesHistoryByCode,
} from 'APIPackage/PublishRulesModule/services/publishRulesApi';
import { IPublishRules } from 'APIPackage/PublishRulesModule/interfaces/IPublishRules';
import { CatalogsActionsEnum } from 'CorePackage/CatalogsModule/enums/CatalogsActionsEnum';
import { getCatalogsState } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { ICatalogsState } from 'CorePackage/CatalogsModule/interfaces/ICatalogsState';
import { CatalogStatusesEnum } from 'CorePackage/CatalogsModule/enums/CatalogStatusesEnum';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';

import { PublishRulesActionsEnum } from '../enums/publishRulesActionsEnum';
import {
  createOrUpdatePublishRules,
  deletePublishRules,
  fetchPublishRules,
  fetchPublishRulesHistory,
  loadPublishRulesData,
  setPublishRulesActiveTreeItem,
  setPublishRulesFormData,
  setPublishRulesFormRef,
  setPublishRulesHistory,
  setPublishRulesIsNeedSaveAccept,
  setPublishRulesNextActiveTreeItem,
  setPublishRulesTree,
  updatePublishRulesActiveTreeItem,
} from '../actions/publishRulesActions';
import { getPublishRulesHistoryFieldsByCategory } from '../common/getPublishRulesHistoryFieldsByCategory';
import {
  getPublishRulesActiveTreeItem,
  getPublishRulesFormData,
  getPublishRulesFormRef,
  getPublishRulesTree,
} from '../selectors/publishRulesSelectors';
import { PublishRulesCategoriesEnum } from '../enums/publishRulesCategoriesEnum';
import { IPublishRulesTreeItem } from '../interfaces/IPublishRulesTreeItem';
import { markTreeWithSavedSettings } from '../utils/markTreeWithSavedSettings';
import { MultipleHistoryChanges, PublishRulesFormRef } from '../reducers/publishRulesReducer';
import { PublishRulesApproveTypeEnum } from '../enums/publishRulesApproveTypeEnum';

export function* watchPublishRulesSaga() {
  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_FETCH_TREE, fetchTreeSaga);
  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_SET_ACTIVE_TREE_ITEM, setActiveTreeItemSaga);
  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_GET_KNM_STATUSES, getKnmStatusesSaga);
  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_GET_ERP_STATUSES, getErpStatusesSaga);
  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_GET_PM_STATUSES, getPmStatusesSaga);

  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_LOAD_DATA, loadPublishRulesDataSaga);
  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_FETCH, fetchPublishRulesSaga);
  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_FETCH_HISTORY, fetchPublishRulesHistorySaga);
  yield takeEvery(
    PublishRulesActionsEnum.PUBLISH_RULES_FETCH_HAVE_SAVED_SETTINGS_CATEGORIES,
    fetchPublishRulesHaveSavedSettingsCategoriesSaga,
  );
  yield takeEvery(
    PublishRulesActionsEnum.PUBLISH_RULES_DROP_STATE_WITH_ACTIVE_TREE_ITEM_CHANGE,
    dropStateWithActiveTreeItemChangeSaga,
  );
  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_CREATE_OR_UPDATE, createOrUpdatePublishRulesSaga);
  yield takeEvery(PublishRulesActionsEnum.PUBLISH_RULES_DELETE, deletePublishRulesSaga);
}

function* waitForCatalog(catalogName: CommonCatalogsNamesEnum, isDeepLoading = false) {
  const catalogs: ICatalogsState = yield select(getCatalogsState);
  const catalog = catalogs && catalogs[catalogName];

  const canSkip =
    catalog && (catalog.status === CatalogStatusesEnum.LOADING || catalog.status === CatalogStatusesEnum.SUCCEEDED);

  if (canSkip) {
    return;
  }

  yield put(catalogGet(catalogName, isDeepLoading));

  yield race({
    success: take(
      (action: ActionType<typeof catalogGetSuccess>) =>
        action.type === CatalogsActionsEnum.CATALOG_GET_SUCCESS && action.payload.catalogName === catalogName,
    ),
    failure: take(
      (action: ActionType<typeof catalogGetFailure>) =>
        action.type === CatalogsActionsEnum.CATALOG_GET_FAILURE && action.payload.catalogName === catalogName,
    ),
  });
}

function* fetchTreeSaga() {
  function* funcTry() {
    // TODO: Реализовать получение дерева
    // const res: IPublishRulesTreeItem[] = yield fetchTree();
    // const defaultItem = res[0];
    // yield put(setPublishRulesTree(res));
    // yield put(setPublishRulesActiveTreeItem(defaultItem));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* setActiveTreeItemSaga(action: ActionType<typeof setPublishRulesActiveTreeItem>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const { formRef, isNeedSaveAccept } = state.publishRules;
    const formState = formRef?.current?.getState();
    const isFormPristine = formState?.pristine;
    const isFormValid = formState?.valid;
    const newActiveTreeItem = action.payload?.item;

    if (!isFormPristine && isFormValid && !isNeedSaveAccept) {
      yield put(setPublishRulesIsNeedSaveAccept(true));
      yield put(setPublishRulesNextActiveTreeItem(newActiveTreeItem));
    } else {
      yield call(dropStateWithActiveTreeItemChangeSaga);
      yield put(updatePublishRulesActiveTreeItem(newActiveTreeItem));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* dropStateWithActiveTreeItemChangeSaga() {
  function* funcTry() {
    yield put(setPublishRulesHistory([]));
    yield put(setPublishRulesNextActiveTreeItem(undefined));
    yield put(setPublishRulesFormRef(undefined));
    yield put(setPublishRulesFormData(undefined));
    yield put(setPublishRulesIsNeedSaveAccept(false));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* loadPublishRulesDataSaga(action: ActionType<typeof loadPublishRulesData>) {
  const { category } = action.payload;

  if (category.includes('KNM')) {
    yield call(getKnmStatusesSaga);
  }

  if (
    category === PublishRulesCategoriesEnum.PM ||
    category === PublishRulesCategoriesEnum.PM_PP ||
    category === PublishRulesCategoriesEnum.PM_OP
  ) {
    yield call(getPmStatusesSaga);
  }

  if (category.includes('PM_CHECKS')) {
    yield call(getErpStatusesSaga);
  }

  yield call(fetchPublishRulesSaga, fetchPublishRules(category));
  yield call(fetchPublishRulesHistorySaga, fetchPublishRulesHistory(category));
}

function* getKnmStatusesSaga() {
  yield* waitForCatalog(CommonCatalogsNamesEnum.KNM_STATUSES_248);
}

function* getPmStatusesSaga() {
  yield* waitForCatalog(CommonCatalogsNamesEnum.PM_STATUSES);
}

function* getErpStatusesSaga() {
  yield* waitForCatalog(CommonCatalogsNamesEnum.KNM_STATUSES_294);
}

function* fetchPublishRulesSaga(action: ActionType<typeof fetchPublishRules>) {
  const { code } = action.payload;

  function* funcTry() {
    const response: IPublishRules = yield fetchPublishRulesByCode(code);

    yield put(setPublishRulesFormData(response));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text || 'Не удалось получить настройки правил публикации',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}

function* fetchPublishRulesHaveSavedSettingsCategoriesSaga() {
  function* funcTry() {
    const treeState: IPublishRulesTreeItem[] = yield select(getPublishRulesTree);
    const activeTreeItemState: IPublishRulesTreeItem = yield select(getPublishRulesActiveTreeItem);

    const response: PublishRulesCategoriesEnum[] = yield fetchPublishRulesHaveSavedSettingsCategories();

    const markedTree = markTreeWithSavedSettings(treeState, response);
    const newActiveTreeItem: IPublishRulesTreeItem = {
      ...activeTreeItemState,
      haveSavedSettings: response?.includes(activeTreeItemState.category),
    };

    yield put(setPublishRulesTree(markedTree));
    yield put(updatePublishRulesActiveTreeItem(newActiveTreeItem));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent:
            currentError.message || currentError.text || 'Не удалось получить категории имеющие сохраненные настройки',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}

function* createOrUpdatePublishRulesSaga(action: ActionType<typeof createOrUpdatePublishRules>) {
  const { code, body } = action.payload;

  function* funcTry() {
    const oldData: IPublishRules = yield select(getPublishRulesFormData);
    const response: IPublishRules = yield createOrUpdatePublishRulesByCode(code, body);

    // Обновляем только entries
    yield put(setPublishRulesFormData({ ...oldData, entries: response.entries }));
    yield all([
      call(fetchPublishRulesHaveSavedSettingsCategoriesSaga),
      call(fetchPublishRulesHistorySaga, fetchPublishRulesHistory(code)),
    ]);
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text || 'Не удалось сохранить',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}

function* deletePublishRulesSaga(action: ActionType<typeof deletePublishRules>) {
  const { code, initialValues } = action.payload;

  function* funcTry() {
    const formRef: PublishRulesFormRef = yield select(getPublishRulesFormRef);

    yield deletePublishRulesByCode(code);
    yield all([
      call(fetchPublishRulesHaveSavedSettingsCategoriesSaga),
      call(fetchPublishRulesHistorySaga, fetchPublishRulesHistory(code)),
    ]);
    formRef?.current.reset(initialValues as Record<string, unknown>);
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text || 'Не удалось удалить',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}

function* fetchPublishRulesHistorySaga(action: ActionType<typeof fetchPublishRulesHistory>) {
  const { code } = action.payload;

  function* funcTry() {
    if (code.includes('UNPLANNED_KNM')) {
      const responseNonRequired: IKnmHistoryItem[] = yield fetchPublishRulesHistoryByCode(
        code,
        PublishRulesApproveTypeEnum.NON_REQUIRED,
      );

      const responsePromptly: IKnmHistoryItem[] = yield fetchPublishRulesHistoryByCode(
        code,
        PublishRulesApproveTypeEnum.PROMPTLY,
      );

      const responseRequired: IKnmHistoryItem[] = yield fetchPublishRulesHistoryByCode(
        code,
        PublishRulesApproveTypeEnum.REQUIRED,
      );

      const adaptedChangesNonRequired: IKnmHistoryItemChanges[] = yield makeHistoryChanges(responseNonRequired);
      const adaptedChangesPromptly: IKnmHistoryItemChanges[] = yield makeHistoryChanges(responsePromptly);
      const adaptedChangesRequired: IKnmHistoryItemChanges[] = yield makeHistoryChanges(responseRequired);

      const multipleHistory: MultipleHistoryChanges = [
        {
          approveType: PublishRulesApproveTypeEnum.NON_REQUIRED,
          changes: adaptedChangesNonRequired,
        },
        {
          approveType: PublishRulesApproveTypeEnum.PROMPTLY,
          changes: adaptedChangesPromptly,
        },
        {
          approveType: PublishRulesApproveTypeEnum.REQUIRED,
          changes: adaptedChangesRequired,
        },
      ];

      yield put(setPublishRulesHistory(multipleHistory));
    } else {
      const response: IKnmHistoryItem[] = yield fetchPublishRulesHistoryByCode(code);

      const adaptedChanges: IKnmHistoryItemChanges[] = yield makeHistoryChanges(response);

      yield put(setPublishRulesHistory(adaptedChanges));
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text || 'Не удалось получить историю изменений',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.INTERNAL_SERVER,
        }),
      );
    },
  });
}

function* makeHistoryChanges(knmHistory: IKnmHistoryItem[]) {
  const newKnmHistoryChanges: IKnmHistoryItemChanges[] = [];
  const state: IRootState = yield select();
  const activeTreeItem = getPublishRulesActiveTreeItem(state);

  knmHistory?.forEach(knmHistoryItem => {
    const title = `Обновлено ${knmHistoryItem.user?.fullName} (${knmHistoryItem.user?.username}) ${formatDate({
      date: knmHistoryItem.utcTimestamp,
      dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
      parseFormat: DATE_FORMAT_ISO_FNS,
    })} (МСК)`;
    const historyChangesText: Array<string> = [];

    knmHistoryItem.changes.forEach(changesItem => {
      const splitFieldName = changesItem.field.split('/');
      const newField = splitFieldName
        .map(item => (uuidRegExp.test(item) || (item !== '' && Number.isInteger(Number(item))) ? '{id}' : item))
        .join('/');

      const field = activeTreeItem?.category
        ? getPublishRulesHistoryFieldsByCategory(activeTreeItem?.category)[newField]
        : undefined;

      if (!field?.active) {
        return;
      }
      const { oldValue, newValue } = changesItem;
      let oldValueText = oldValue;
      let newValueText = newValue;

      if (field.renderFunc) {
        const { renderedOldValue, renderedNewValue } = field.renderFunc(changesItem, state);

        oldValueText = renderedOldValue;
        newValueText = renderedNewValue;
      }

      const additionalRulesPathPosition = splitFieldName.findIndex(item => item === 'additionalRules');
      const additionalRulesIndexPosition =
        additionalRulesPathPosition !== -1 ? additionalRulesPathPosition + 1 : undefined;
      const additionalRulesIndex =
        additionalRulesIndexPosition !== undefined ? Number(splitFieldName[additionalRulesIndexPosition]) : undefined;

      let valuesText = '';

      if (changesItem.operation === KnmHistoryOperationsEnum.INSERT) {
        valuesText = `"${newValueText}"`;
      } else if (changesItem.operation === KnmHistoryOperationsEnum.UPDATE) {
        valuesText = `${oldValueText ? `с "${oldValueText}" ` : ''}на "${newValueText}"`;
      } else if (changesItem.operation === KnmHistoryOperationsEnum.DELETE) {
        valuesText = `"${oldValueText}"`;
      }

      const changeText = `${KNM_HISTORY_FIELD_TEXT_PREFIX} "${field.name}" ${
        knmHistoryFieldOperationsMap[changesItem.operation]
      } ${valuesText}${
        additionalRulesIndex !== undefined ? ` (Дополнительное правило ${additionalRulesIndex + 1})` : ''
      }`;

      historyChangesText.push(changeText);
    });
    if (historyChangesText.length) {
      newKnmHistoryChanges.push({
        title,
        changesText: historyChangesText,
        eventDate: knmHistoryItem.utcTimestamp,
      });
    }
  });

  return newKnmHistoryChanges;
}
